import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  DsButtonV2Component,
  DsCardComponent,
  DsInlineAlertComponent,
  DsInputTextComponent,
  DsTabButtonComponent,
} from '@customdesignsystem';
import {
  CURRENCY_UNITS,
  STEP_ONE,
} from '../../constants/amount.constant';
import { SUBSCRIPTION_URLS } from '../../constants/subscription-routes.constant';
import { StepperLateralComponent } from '../../components/stepper-lateral/stepper-lateral.component';
import { SubscriptionApiService } from '../../services/subscription-api.service';
import { SubscriptionStore } from '../../services/subscription.store';
import { toClp } from '../../../core/utils/formatters';

const CURRENT_STEP_INDEX = 0;
const PESOS_MAX_DIGITS = 9;
const UF_MAX_DIGITS = 2;

@Component({
  selector: 'app-amount-page',
  templateUrl: './amount.page.html',
  styleUrl: './amount.page.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepperLateralComponent,
    DsButtonV2Component,
    DsCardComponent,
    DsInlineAlertComponent,
    DsInputTextComponent,
    DsTabButtonComponent,
  ],
})
export class AmountPage implements OnInit {
  stepOne = STEP_ONE;
  units = CURRENCY_UNITS;
  readonly currentStepIndex = CURRENT_STEP_INDEX;

  ufValue: string = '';
  isEditStepper: boolean = false;

  frmStepOne: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly api: SubscriptionApiService,
    private readonly store: SubscriptionStore
  ) {
    this.frmStepOne = this.formBuilder.group({
      currency: ['pesos', Validators.compose([Validators.required])],
      amount: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(11),
        ]),
      ],
    });
  }

  ngOnInit(): void {
    this.getAndSetCurrentUf();
    this.checkQueryParams();
    this.loadSavedData();
    this.isEditStepper = this.store.editMode();
  }

  private checkQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['amount'] && params['currency']) {
        this.frmStepOne.patchValue({
          currency: params['currency'] === 'U' ? 'uf' : 'pesos',
          amount: params['amount'],
        });
        this.frmStepOne.get('amount')?.markAsTouched();
      }
    });
  }

  private loadSavedData(): void {
    const data = this.store.amount();
    if (data) {
      this.frmStepOne.patchValue(data);
    }
  }

  get selectedUnit() {
    return (
      this.units.find((unit) => unit.id === this.frmStepOne.value.currency) ??
      this.units[0]
    );
  }

  get isUfSelected(): boolean {
    return this.frmStepOne.value.currency === 'uf';
  }

  get canContinue(): boolean {
    return this.frmStepOne.valid;
  }

  get hasAmountError(): boolean {
    const amount = this.frmStepOne.get('amount');
    return !!amount?.errors && !!amount?.touched;
  }

  get amountHelpText(): string {
    if (this.hasAmountError) return this.selectedUnit.errorText;
    if (this.isUfSelected && this.ufValue) {
      return `1 UF equivale a $${this.ufValue} / ${this.selectedUnit.helpText}`;
    }
    return this.selectedUnit.helpText;
  }

  selectUnit(unitId: string): void {
    if (this.frmStepOne.value.currency === unitId) return;

    this.frmStepOne.get('currency')?.setValue(unitId);
    this.addDeleteValidatorAmount();
  }

  get amountDigits(): string {
    return String(this.frmStepOne.value.amount ?? '').replaceAll('.', '');
  }

  get amountMaxDigits(): number {
    return this.isUfSelected ? UF_MAX_DIGITS : PESOS_MAX_DIGITS;
  }

  onAmountChange(digits: string): void {
    this.frmStepOne.get('amount')?.setValue(this.numberToCurrency(digits));
  }

  onAmountBlur(): void {
    this.frmStepOne.get('amount')?.markAsTouched();
  }

  private async getAndSetCurrentUf(): Promise<void> {
    try {
      this.ufValue = await this.api.getCurrentUf();
    } catch (error: any) {
      console.log('Error getting uf');
      console.log(error.message);
    }
  }

  addDeleteValidatorAmount(): void {
    this.frmStepOne.get('amount')?.setValue('');
    this.frmStepOne.get('amount')?.markAsUntouched();

    if (this.frmStepOne.get('currency')?.value === 'pesos') {
      this.frmStepOne
        .get('amount')
        ?.setValidators([
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(11),
        ]);
    } else {
      this.frmStepOne
        .get('amount')
        ?.setValidators([
          Validators.required,
          Validators.maxLength(2),
          Validators.min(1),
          Validators.max(50),
        ]);
    }
    this.frmStepOne.get('amount')?.updateValueAndValidity();
  }

  public numberToCurrency(number: string): string {
    if (number.length > 9) {
      number = number.slice(0, 9);
    }
    if (number.startsWith('0')) {
      number = number.replace('0', '');
    }
    if (number) {
      const numberWithDots = toClp(Number(number));
      return numberWithDots;
    }
    return '';
  }

  goToNextStep(): void {
    if (!this.canContinue) return;

    this.saveData();
    this.router.navigate([SUBSCRIPTION_URLS.regimeFund]);
  }

  goBack(): void {
    if (this.isEditStepper) {
      this.backToConfirmation();
      return;
    }

    this.router.navigate([SUBSCRIPTION_URLS.apvHome]);
  }

  saveChanges(): void {
    if (!this.canContinue) return;

    this.saveData();
    this.backToConfirmation();
  }

  private backToConfirmation(): void {
    this.store.setEditMode(false);
    this.router.navigate([SUBSCRIPTION_URLS.confirm]);
  }

  private saveData(): void {
    this.store.setAmount({
      currency: this.frmStepOne.value.currency,
      amount: this.frmStepOne.value.amount,
    });
  }
}
